import { Fragment, useEffect, useState } from 'react';

import { useSuspenseXinglet } from '../hooks/use-suspense-xinglet';
import type { Route } from '../route-context';
import type { XingletLoaderProps } from '../xinglet';

import type { Runtime } from './runtime';
import { useRuntime } from './use-runtime';

const XINGLET_INSPECTOR_STORAGE_KEY = 'crate-xinglet-inspector-enabled';

const EmptyErrorComponent: React.FC<{ name: string; route?: Route }> = ({
  name,
  route,
}) => {
  if (route) {
    throw new Error(
      `Xinglet "${name}" didn't provide a component for route ${route.route}.`
    );
  } else {
    throw new Error(`Xinglet "${name}" didn't export any component.`);
  }
};

const useXingletInspectorEnabled = (): boolean => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    setEnabled(getBrowserInspectorValue());
  }, []);

  return enabled;
};

const getBrowserInspectorValue = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return (
      window.localStorage.getItem(XINGLET_INSPECTOR_STORAGE_KEY) === 'true'
    );
  } catch {
    return false;
  }
};

const XingletInspectorMarkers: React.FC<
  React.PropsWithChildren<{ enabled: boolean; name: string }>
> = ({ children, enabled, name }) => {
  return [
    enabled ? (
      <span
        key="start"
        data-crate-xinglet-start={name}
        hidden
        aria-hidden="true"
      />
    ) : null,
    <Fragment key="content">{children}</Fragment>,
    enabled ? (
      <span key="end" data-crate-xinglet-end={name} hidden aria-hidden="true" />
    ) : null,
  ];
};

const RawXingletLoader: React.FC<{
  name: string;
  route?: Route;
  withInspectorMarkers?: boolean;
}> = ({ name, route, withInspectorMarkers = false, ...rest }) => {
  const { Component: MainComponent, module } = useSuspenseXinglet(name);
  const { routes } = module;

  const Component = route && routes ? routes[route.route] : MainComponent;

  if (!Component) return <EmptyErrorComponent name={name} route={route} />;

  const content = <Component {...rest} />;
  return (
    <XingletInspectorMarkers enabled={withInspectorMarkers} name={name}>
      {content}
    </XingletInspectorMarkers>
  );
};

const SSRWrapper: React.FC<
  React.PropsWithChildren<{
    fallback?: React.ReactNode;
    name: string;
    runtime: Runtime;
  }>
> = ({ children, fallback, name, runtime }) => {
  const { manifestMap } = runtime;
  const ssr = manifestMap[name]?.metadata?.ssr === true;
  const [enabled, setEnabled] = useState(ssr);

  useEffect(() => setEnabled(true), []);

  return <>{enabled ? children : fallback}</>;
};

type InternalXingletLoader = XingletLoaderProps & { route?: Route };

export const InternalXingletLoader: React.FC<InternalXingletLoader> = ({
  fallback,
  name,
  route,
  ...rest
}) => {
  const runtime = useRuntime();
  const withInspectorMarkers = useXingletInspectorEnabled();

  /* oxlint-disable eslint-plugin-react-hooks/rules-of-hooks */
  if (LOCAL_DEVELOPMENT) {
    const [, setCount] = useState(0);

    useEffect(() => {
      return runtime.events.on('xinglet-reloaded', (otherName) => {
        if (otherName === name) setCount((count) => count + 1);
      });
    }, [runtime, name]);
  }
  /* oxlint-enable eslint-plugin-react-hooks/rules-of-hooks */

  return (
    <SSRWrapper fallback={fallback} name={name} runtime={runtime}>
      <RawXingletLoader
        name={name}
        route={route}
        withInspectorMarkers={withInspectorMarkers}
        {...rest}
      />
    </SSRWrapper>
  );
};
